/**
 * Friendly recovery hints for errors surfaced in the Send/Swap screens.
 *
 * The raw `e.message` is technically accurate but often unhelpful
 * ("SaplingBuild(AnchorMismatch)" doesn't tell a user what to do).
 * This module maps known-shape errors to a one-line headline + a
 * one-line suggestion the user can act on.
 *
 * Falls back to the raw message when nothing matches — the UI shows
 * both the hint and the raw message so we don't hide debug info.
 */

export interface ErrorHint {
  /** Short, user-facing headline. */
  headline: string;
  /** One-line actionable suggestion, or null if none. */
  suggestion: string | null;
}

const PATTERNS: Array<{
  match: RegExp;
  headline: string;
  suggestion: string;
}> = [
  // Send pipeline (SendShieldedError codes)
  {
    match: /no spendable shielded notes/i,
    headline: "No shielded balance yet",
    suggestion: "Wait for the scanner to finish syncing your wallet, or deposit some ZEC first.",
  },
  {
    match: /insufficient_funds|have \d+ zats? across/i,
    headline: "Not enough shielded balance",
    suggestion: "Your spendable notes don't cover the amount plus the ZIP-317 fee. Try a smaller amount.",
  },
  {
    match: /amount must be positive|zero_amount/i,
    headline: "Invalid amount",
    suggestion: "Enter an amount greater than zero.",
  },
  {
    match: /worker likely stalled|PARAMS_TIMEOUT|took longer than \d+s/i,
    headline: "Proving-keys setup stalled",
    suggestion: "iOS sometimes suspends background workers. Close the wallet (X), reopen it, and try again — the partially-decompressed shard is cached so the retry is much faster.",
  },
  {
    match: /params|sapling-(spend|output)\.params/i,
    headline: "Couldn't load proving keys",
    suggestion: "Check your network connection. First-time setup needs to fetch ~26 MB of Sapling params.",
  },
  // Proof / WASM errors
  {
    match: /out of memory|RangeError|wasm.*memory/i,
    headline: "Device ran out of memory mid-proof",
    suggestion: "Sapling proofs need ~150–200 MB of RAM. Close other tabs and retry, or try a smaller send.",
  },
  {
    match: /SaplingBuild\(.*Anchor/i,
    headline: "Witness sync got stale",
    suggestion: "Tap Try again — the wallet will re-fetch a fresh anchor + witnesses before signing.",
  },
  {
    match: /SaplingBuild|build_sapling_tx_v5|proof_failed/i,
    headline: "Couldn't generate proof",
    suggestion: "Try again. If it keeps failing, share the error message in the bug report.",
  },
  // Broadcast (shielded ZEC relay). Narrowed to avoid catching the
  // LeoKit-specific HTTP error shapes below — they look like
  // "LeoKit /quote → HTTP 5xx" and want different copy.
  {
    match: /broadcast.*failed|broadcast_failed|relay.*HTTP 5\d\d/i,
    headline: "Couldn't broadcast",
    suggestion: "The shielded relay is unreachable. Wait a minute and try again — the signed tx is held until then.",
  },
  // Bot / network
  {
    match: /rate limit/i,
    headline: "Slow down",
    suggestion: "You're hitting the bot rate limit. Try again in about a minute.",
  },
  {
    match: /failed to fetch|NetworkError|ERR_/i,
    headline: "Network unreachable",
    suggestion: "Check your connection. If you're on cellular, switch to Wi-Fi.",
  },
  // MAYA
  {
    match: /MAYA.*halted|ZEC pool is halted/i,
    headline: "MAYA's ZEC pool is paused",
    suggestion: "Try again later — the pool periodically halts for vault rotations.",
  },
  {
    match: /shielded_memo_config is not enabled/i,
    headline: "MAYA shielded-memo swap unavailable",
    suggestion: "The protocol hasn't enabled this swap mode right now. Use a transparent ZEC wallet for swaps until it returns.",
  },
  // LeoKit aggregator — most often "amount too small" or "no liquidity"
  // for the requested pair. We can't tell them apart from the wire
  // message, so the suggestion covers both.
  {
    match: /no mayachain offer|no protocol could route/i,
    headline: "No route for this swap",
    suggestion: "Try a larger amount, a different destination asset, or wait a minute — protocol pools occasionally drop out and come back.",
  },
  {
    match: /UNABLE_TO_RETRIEVE_QUOTES|leokit.*\/quote.*HTTP 404/i,
    headline: "Couldn't find a quote",
    suggestion: "The aggregator returned no offers. Most often this means the amount is below a protocol's minimum (~$10 for MAYA, $1 for others). Try a larger amount.",
  },
  {
    match: /leokit.*\/quote.*HTTP (5\d\d|429)/i,
    headline: "Aggregator is busy",
    suggestion: "LeoKit is rate-limiting or temporarily down. Wait a few seconds and try again.",
  },
  {
    match: /leokit.*\/deposit.*missing|quote_id missing|leokit.*missing unsigned/i,
    headline: "Quote expired",
    suggestion: "The quote you confirmed has aged out. Hit Cancel, then re-submit so a fresh quote can bind.",
  },
];

export function explainError(message: string): ErrorHint {
  for (const p of PATTERNS) {
    if (p.match.test(message)) {
      return { headline: p.headline, suggestion: p.suggestion };
    }
  }
  return {
    headline: "Something went wrong",
    suggestion: null,
  };
}
